import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { BOOKS, getBook } from "../data/books";
import { MODELS, getModel } from "../data/models";
import { BookCover } from "../components/BookCover";
import { Character } from "../components/Character";
import { useApp } from "../context/AppContext";
import { FORMATS, type FormatId } from "@shared/pricing";
import { personalizeItemSchema, type PersonalizeItem } from "@shared/schemas";
import {
  type PersonalizeDraft,
  clearDraftFragment,
  decodeDraft,
  encodeDraft,
  readDraftFromLocation,
  writeDraftFragment,
} from "../lib/url";
import type { CartEntry } from "../lib/cart";

function LinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

const STEPS = [
  { key: "name", label: "Name" },
  { key: "gender", label: "Boy/Girl" },
  { key: "model", label: "Character" },
  { key: "dedication", label: "Dedication" },
  { key: "signature", label: "From" },
  { key: "format", label: "Format" },
] as const;

type Gender = PersonalizeItem["gender"];

type EditState = { editItemId: string; editItem: CartEntry } | undefined;

export function PersonalizeFlow({ bookId }: { bookId: string }) {
  const { go, addToCart, updateCartItem } = useApp();
  const location = useLocation();
  const editState = location.state as EditState;
  const editItemId = editState?.editItemId ?? null;
  const book = getBook(bookId) ?? BOOKS[0]!;

  const [step, setStep] = useState(0);
  const [showCover, setShowCover] = useState(false);
  const [name, setName] = useState("");
  const [gender, setGender] = useState<Gender | null>(null);
  const [modelId, setModelId] = useState<string | null>(null);
  const [dedication, setDedication] = useState("");
  const [signature, setSignature] = useState("");
  const [format, setFormat] = useState<FormatId | null>(null);
  const [shareToast, setShareToast] = useState<string | null>(null);

  // Hydrate on mount. Route state (edit mode) takes priority over URL draft.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (editState?.editItem) {
      const ei = editState.editItem;
      setName(ei.name);
      setGender(ei.gender);
      setModelId(ei.modelId);
      setDedication(ei.dedication);
      setSignature(ei.signature);
      setFormat(ei.format as FormatId);
      setStep(STEPS.length - 1);
      hydratedRef.current = true;
      return;
    }
    const blob = readDraftFromLocation();
    if (!blob) {
      hydratedRef.current = true;
      return;
    }
    void decodeDraft(blob).then((draft) => {
      if (draft) applyDraft(draft);
      hydratedRef.current = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyDraft(draft: PersonalizeDraft) {
    if (typeof draft.step === "number") setStep(Math.min(draft.step, STEPS.length - 1));
    if (typeof draft.name === "string") setName(draft.name);
    if (draft.gender !== undefined) setGender(draft.gender ?? null);
    if (draft.modelId !== undefined) setModelId(draft.modelId ?? null);
    if (typeof draft.dedication === "string") setDedication(draft.dedication);
    if (typeof draft.signature === "string") setSignature(draft.signature);
    if (draft.format !== undefined) setFormat(draft.format ?? null);
  }

  // Debounced write of the current state into the URL fragment. Uses
  // replaceState (via writeDraftFragment), so the back button does NOT
  // step through wizard keystrokes — it jumps back to the previous page.
  useEffect(() => {
    if (!hydratedRef.current) return;
    const id = setTimeout(() => {
      const draft: PersonalizeDraft = {
        bookId: book.id as PersonalizeDraft["bookId"],
        step,
        name,
        gender,
        modelId,
        dedication,
        signature,
        format,
      };
      void encodeDraft(draft).then((blob) => writeDraftFragment(blob));
    }, 300);
    return () => clearTimeout(id);
  }, [book.id, step, name, gender, modelId, dedication, signature, format]);

  useEffect(() => {
    if (name && !dedication) {
      setDedication(book.dedicationDefault.replace("{NAME}", name));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  const onShareDraft = async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      setShareToast("Copy this URL from the address bar.");
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setShareToast("Copied!");
      } catch {
        setShareToast("Could not copy — select and copy the URL manually.");
      }
    }
    setTimeout(() => setShareToast(null), 2200);
  };

  const model = getModel(modelId);
  const totalSteps = STEPS.length;
  const stepLabel = step === totalSteps - 1 ? "Last Step!" : `Step ${step + 1} of ${totalSteps}`;

  const canContinue = (): boolean => {
    if (showCover) return true;
    if (step === 0) return name.trim().length > 0;
    if (step === 1) return !!gender;
    if (step === 2) return !!modelId;
    if (step === 3) return dedication.trim().length > 0;
    if (step === 4) return signature.trim().length > 0;
    if (step === 5) return !!format;
    return false;
  };

  const next = () => {
    // After character step, show the cover preview (doesn't advance counter).
    if (step === 2 && !showCover) {
      setShowCover(true);
      return;
    }
    if (showCover) {
      setShowCover(false);
      setStep(3);
      return;
    }
    if (step === STEPS.length - 1) {
      if (!gender || !modelId || !format) return;
      const item: PersonalizeItem = {
        bookId: book.id,
        name: name.trim(),
        gender,
        modelId,
        dedication: dedication.trim(),
        signature: signature.trim(),
        format,
      };
      const parsed = personalizeItemSchema.safeParse(item);
      if (!parsed.success) return;
      if (editItemId) {
        updateCartItem(editItemId, parsed.data);
      } else {
        addToCart(parsed.data);
      }
      clearDraftFragment();
      go({ name: "cart" });
      return;
    }
    setStep(step + 1);
  };

  const back = () => {
    if (showCover) {
      setShowCover(false);
      return;
    }
    if (step === 0) {
      go(editItemId ? { name: "cart" } : { name: "detail", bookId: book.id });
      return;
    }
    setStep(step - 1);
  };

  return (
    <div className="flow">
      <div className="flow-top">
        <div className="wrap flow-top-row">
          <a
            className="exit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              clearDraftFragment();
              go(editItemId ? { name: "cart" } : { name: "detail", bookId: book.id });
            }}
          >
            ← Exit
          </a>
          <div className="flow-progress">
            <span className="step-count">
              Step {step + 1} / {STEPS.length}
            </span>
            <div className="bar">
              {STEPS.map((s, i) => (
                <div key={s.key} className={`seg ${i < step ? "done" : i === step ? "on" : ""}`} />
              ))}
            </div>
          </div>
          <button
            type="button"
            className="share-btn"
            onClick={() => void onShareDraft()}
            title="Copy a shareable link to this draft"
          >
            {shareToast ?? "Copy"}{!shareToast && <LinkIcon />}
          </button>
        </div>
      </div>

      <div className="flow-body">
        <div className="flow-side">
          <div className="stage-cover">
            <BookCover book={book} name={name} model={step >= 2 || showCover ? model : undefined} />
          </div>
        </div>

        <div className="flow-main">
          {step === 0 && (
            <>
              <div className="step-n">{stepLabel} — Their name</div>
              <h2>
                What's their <span className="it">name?</span>
              </h2>
              <p className="hint">
                This is the name that goes on the cover, and pops up seven times inside. Nicknames welcome. Up to 12
                characters.
              </p>
              <input
                className="text-input"
                placeholder="Type their name…"
                value={name}
                maxLength={12}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </>
          )}

          {step === 1 && (
            <>
              <div className="step-n">{stepLabel} — A small choice</div>
              <h2>
                Is <span className="it">{name}</span> a boy or a girl?
              </h2>
              <p className="hint">
                This helps us choose pronouns inside the book. You can also skip this — we'll use their name throughout
                instead.
              </p>
              <div className="choice-row">
                <button className={`choice-card ${gender === "boy" ? "on" : ""}`} onClick={() => setGender("boy")}>
                  <span className="sub">— He / Him</span>
                  <span className="label">Boy</span>
                </button>
                <button className={`choice-card ${gender === "girl" ? "on" : ""}`} onClick={() => setGender("girl")}>
                  <span className="sub">— She / Her</span>
                  <span className="label">Girl</span>
                </button>
              </div>
              <button
                className={`choice-card ${gender === "neutral" ? "on" : ""}`}
                style={{
                  width: "100%",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
                onClick={() => setGender("neutral")}
              >
                <span className="label" style={{ fontSize: 22 }}>
                  Use their name throughout
                </span>
                <span className="sub">— They / Them</span>
              </button>
            </>
          )}

          {step === 2 && !showCover && (
            <>
              <div className="step-n">{stepLabel} — Their character</div>
              <h2>
                Pick a <span className="it">character</span> that looks like {name || "them"}.
              </h2>
              <p className="hint">Choose one of these character models to represent your child in the book.</p>
              <div className="model-grid">
                {MODELS.map((m) => (
                  <button
                    key={m.id}
                    className={`model-card ${modelId === m.id ? "on" : ""}`}
                    onClick={() => setModelId(m.id)}
                  >
                    <svg viewBox="0 0 100 100" className="avatar" style={{ background: "var(--cream-deep)" }}>
                      <rect width="100" height="100" fill="var(--cream-deep)" />
                      <Character x={50} y={50} model={m} scale={2.8} />
                    </svg>
                  </button>
                ))}
              </div>
            </>
          )}

          {showCover && (
            <>
              <div className="step-n">THE COVER</div>
              <h2>
                Meet <span className="it">{name}'s</span> book.
              </h2>
              <p className="hint">
                Here's how the cover looks. Like it? Continue to write a dedication. Want to tweak the character? Step
                back.
              </p>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  fontFamily: "var(--mono)",
                  fontSize: 13,
                  color: "var(--ink-soft)",
                  letterSpacing: "0.06em",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed var(--line)", paddingBottom: 8 }}>
                  <span>NAME</span>
                  <span style={{ fontFamily: "var(--serif)", fontWeight: 500, color: "var(--ink)", fontSize: 18 }}>{name}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed var(--line)", paddingBottom: 8 }}>
                  <span>PRONOUNS</span>
                  <span style={{ fontFamily: "var(--serif)", fontWeight: 500, color: "var(--ink)", fontSize: 18 }}>
                    {gender === "boy" ? "He / Him" : gender === "girl" ? "She / Her" : "They / Them"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px dashed var(--line)", paddingBottom: 8 }}>
                  <span>CHARACTER</span>
                  <span style={{ fontFamily: "var(--serif)", fontWeight: 500, color: "var(--ink)", fontSize: 18 }}>As pictured</span>
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="step-n">{stepLabel} — Your dedication</div>
              <h2>
                Write something <span className="it">only you</span> could write.
              </h2>
              <p className="hint">
                We've started one for you. Edit it freely — this prints on the inside cover, just for {name}.
              </p>
              <textarea
                className="flow-area"
                value={dedication}
                onChange={(e) => setDedication(e.target.value)}
                maxLength={280}
              />
              <div
                style={{
                  marginTop: 8,
                  fontFamily: "var(--mono)",
                  fontSize: 11,
                  color: "var(--ink-soft)",
                  letterSpacing: "0.1em",
                }}
              >
                {dedication.length} / 280
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="step-n">{stepLabel} — Sign it</div>
              <h2>
                Who <span className="it">made</span> this book?
              </h2>
              <p className="hint">
                Mama, Daddy, Grandma Lily, Auntie Sue, the whole class at Lincoln Elementary — whoever made this happen
                gets their name on the last page.
              </p>
              <input
                className="flow-text-sm"
                value={signature}
                placeholder="With love, from…"
                onChange={(e) => setSignature(e.target.value)}
                maxLength={60}
              />
            </>
          )}

          {step === 5 && (
            <>
              <div className="step-n">{stepLabel} — Pick a format</div>
              <h2>
                How should we <span className="it">print</span> it?
              </h2>
              <p className="hint">
                Every book is printed to order, sewn and trimmed in one of our partner factories in the US, Canada or
                UK. Pick the binding that's right for {name || "your reader"}.
              </p>
              <div className="format-list">
                {Object.values(FORMATS).map((f) => (
                  <button
                    key={f.id}
                    className={`format-card ${format === f.id ? "on" : ""}`}
                    onClick={() => setFormat(f.id as FormatId)}
                  >
                    <span className="fmt-left">
                      <span className="fmt-name">{f.name}</span>
                      <span className="fmt-sub">{f.sub}</span>
                    </span>
                    <span className="fmt-right">
                      <span className="fmt-price">${(f.priceCents / 100).toFixed(0)}</span>
                      <span className={`fmt-dot ${format === f.id ? "on" : ""}`}></span>
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="flow-cta">
            <button className="back" onClick={back}>
              ← back
            </button>
            <button className="btn btn-rust big" onClick={next} disabled={!canContinue()}>
              {step === STEPS.length - 1 && !showCover
                ? editItemId
                  ? "Save changes"
                  : "Add to cart"
                : "Continue"}{" "}
              <span className="arr">→</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
